type Client = {getDisplayCurrency(base: string, amount: string, target?: string, country?: string): Promise<any>};

/**
 * Authoritatively get live USD to INR exchange rate configured by operator in Admin.
 * Checks localStorage cache first, falling back to 95.00 live default.
 */
export function getLiveUsdInrRate(): number {
  if (typeof localStorage !== 'undefined') {
    const cached = localStorage.getItem('starx_fx_USD_INR');
    if (cached) {
      const parsed = parseFloat(cached);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
  }
  return 95.00;
}

/**
 * Authoritatively get live exchange rate of USD against any currency (1 USD = X Currency).
 * Checks localStorage cache first, falling back to institutional defaults.
 */
export function getLiveUsdFxRate(currency: string): number {
  const c = (currency || 'USD').toUpperCase().trim();
  if (c === 'USD' || c === 'USDT' || c === 'USDC' || !c) return 1.0;
  if (c === 'INR') return getLiveUsdInrRate();

  if (typeof localStorage !== 'undefined') {
    const cached = localStorage.getItem(`starx_fx_USD_${c}`);
    if (cached) {
      const parsed = parseFloat(cached);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
  }

  // Institutional standard defaults
  const fallbacks: Record<string, number> = {
    BRL: 5.12,
    EUR: 0.88,
    GBP: 0.75,
    IDR: 17800.0,
    AED: 3.6725,
    TRY: 48.80,
    NGN: 1330.0,
    JPY: 158.0,
    CAD: 1.41,
    AUD: 1.42,
    ZAR: 16.35,
    PKR: 277.0,
    BDT: 120.0,
    CHF: 0.88,
    VND: 25400.0,
    MYR: 4.40,
    THB: 34.50,
    PHP: 58.50,
    MXN: 20.30,
    COP: 4400.0,
    CLP: 940.0,
    EGP: 49.0,
    UZS: 11800.0,
    KZT: 490.0,
    UGX: 3700.0,
    TZS: 2650.0,
    KES: 130.0,
    GHS: 15.0,
    KRW: 1380.0,
    IQD: 1310.0,
  };
  return fallbacks[c] || 1.0;
}

export function getCurrencySymbol(curr?: string): string {
  const c = (curr || 'USD').toUpperCase().trim();
  switch (c) {
    case 'INR': return '₹';
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'CAD': return 'CA$';
    case 'AUD': return 'A$';
    case 'NZD': return 'NZ$';
    case 'CHF': return 'CHF ';
    case 'BRL': return 'R$';
    case 'AED': return 'AED ';
    case 'SAR': return '﷼ ';
    case 'QAR': return 'QR ';
    case 'KWD': return 'KD ';
    case 'ZAR': return 'R ';
    case 'NGN': return '₦';
    case 'GHS': return 'GH₵ ';
    case 'KES': return 'KSh ';
    case 'IDR': return 'Rp ';
    case 'PHP': return '₱';
    case 'BDT': return '৳';
    case 'PKR': return '₨ ';
    case 'MXN': return 'Mex$';
    case 'TRY': return '₺';
    case 'KRW': return '₩';
    case 'HKD': return 'HK$';
    case 'TWD': return 'NT$';
    case 'SGD': return 'S$';
    case 'PLN': return 'zł ';
    case 'SEK': case 'NOK': case 'DKK': return 'kr ';
    case 'UZS': return "so'm ";
    case 'VND': return '₫';
    case 'COP': return 'COL$';
    case 'CLP': return 'CLP$';
    case 'KZT': return '₸';
    case 'UGX': return 'USh ';
    case 'TZS': return 'TSh ';
    case 'EGP': return 'E£ ';
    case 'USD': case 'USDT': case 'USDC': return '$';
    default: {
      const sym = WORLD_CURRENCY_DATA[c]?.symbol;
      if (sym) {
        return sym.endsWith(' ') || sym.length === 1 ? sym : `${sym} `;
      }
      return `${c} `;
    }
  }
}

export interface StakePresetsInfo {
  presets: number[];
  minStake: number;
  maxStake: number;
  stepStake: number;
}

export function getCurrencyStakePresets(currency: string): StakePresetsInfo {
  const curr = (currency || 'USD').toUpperCase().trim();
  const rate = getLiveUsdFxRate(curr);

  // Exact custom market tunings ($1,000 USD equivalent max single stake)
  if (curr === 'INR') {
    return { presets: [100, 500, 1000, 5000], minStake: 70, maxStake: 95000, stepStake: 50 };
  }
  if (curr === 'BRL') {
    return { presets: [25, 50, 100, 250], minStake: 5, maxStake: 5000, stepStake: 5 };
  }
  if (curr === 'JPY') {
    return { presets: [200, 500, 1000, 5000], minStake: 150, maxStake: 160000, stepStake: 100 };
  }
  if (curr === 'UZS') {
    return { presets: [15000, 50000, 100000, 500000], minStake: 10000, maxStake: 11800000, stepStake: 10000 };
  }
  if (curr === 'VND') {
    return { presets: [25000, 100000, 250000, 1000000], minStake: 25000, maxStake: 25400000, stepStake: 25000 };
  }
  if (curr === 'IDR') {
    return { presets: [20000, 50000, 100000, 500000], minStake: 15000, maxStake: 17800000, stepStake: 15000 };
  }
  if (curr === 'KRW') {
    return { presets: [1500, 5000, 10000, 50000], minStake: 1300, maxStake: 1380000, stepStake: 1000 };
  }
  if (curr === 'NGN') {
    return { presets: [1000, 2500, 5000, 20000], minStake: 1000, maxStake: 1330000, stepStake: 500 };
  }
  if (curr === 'PKR' || curr === 'BDT') {
    return { presets: [250, 500, 1000, 5000], minStake: 200, maxStake: 280000, stepStake: 100 };
  }
  if (curr === 'COP') {
    return { presets: [5000, 20000, 50000, 200000], minStake: 4000, maxStake: 4400000, stepStake: 5000 };
  }

  // Universal dynamic formula based on FX rate relative to USD ($1,000 USD default max stake)
  if (rate <= 1.5) {
    return { presets: [10, 25, 50, 100], minStake: 1, maxStake: 1000, stepStake: 5 };
  } else if (rate <= 15) {
    return { presets: [25, 50, 100, 250], minStake: 5, maxStake: Math.round(rate * 1000), stepStake: 5 };
  } else if (rate <= 200) {
    const base = Math.round(rate);
    return {
      presets: [Math.round(base), Math.round(base * 5), Math.round(base * 10), Math.round(base * 50)],
      minStake: Math.max(1, Math.round(base * 0.7)),
      maxStake: Math.round(base * 1000),
      stepStake: Math.max(1, Math.round(base * 0.5)),
    };
  } else if (rate <= 2000) {
    const base = Math.round(rate / 100) * 100;
    return {
      presets: [base, Math.round(base * 2.5), base * 5, base * 20],
      minStake: base,
      maxStake: base * 1000,
      stepStake: Math.round(base / 2),
    };
  } else {
    const base = Math.round(rate / 1000) * 1000;
    return {
      presets: [base, Math.round(base * 2.5), base * 5, base * 25],
      minStake: base,
      maxStake: base * 1000,
      stepStake: base,
    };
  }
}

/**
 * Cache an entire map of world fiat rates into localStorage.
 */
export function cacheWorldFxRates(fiatRates: Record<string, number | string>) {
  if (typeof localStorage === 'undefined' || !fiatRates) return;
  for (const [k, v] of Object.entries(fiatRates)) {
    const cleanK = k.replace(/^USD\//, '').toUpperCase();
    const num = parseFloat(String(v));
    if (!isNaN(num) && num > 0) {
      localStorage.setItem(`starx_fx_USD_${cleanK}`, String(num));
    }
  }
}

/**
 * Fetch latest FX rates from /api/v1/display-currency and cache in localStorage.
 */
export async function fetchAndCacheFxRates(client: Client): Promise<number> {
  try {
    const res = await client.getDisplayCurrency('USD', '1', '');
    if (res?.rates) {
      cacheWorldFxRates(res.rates);
      if (res.rates['USD/INR']) {
        const parsed = parseFloat(res.rates['USD/INR']);
        if (!isNaN(parsed) && parsed > 0) {
          localStorage.setItem('starx_fx_USD_INR', String(parsed));
          return parsed;
        }
      }
    }
  } catch (err) {
    // Non-fatal, use fallback
  }
  return getLiveUsdInrRate();
}

// Funding-account selection is separate from display conversion: no funds move.
export async function chooseFundingWallet(
  client: {getBalance(currency: string): Promise<any>},
  selected: (currency: string) => void,
  extraCurrencies: string[] = []
) {
  const dialog=document.createElement('dialog');
  dialog.style.cssText='position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);margin:0;background:#0c121c;color:#f8fafc;padding:24px;border:1px solid #1e293b;border-radius:16px;box-shadow:0 25px 60px rgba(0,0,0,0.85),0 0 24px rgba(56,189,248,0.15);max-width:440px;width:90vw;z-index:9999;font-family:inherit;';
  const title=document.createElement('h3');title.style.cssText='margin:0 0 8px;font-size:16px;font-weight:700;color:#ffffff;display:flex;align-items:center;gap:8px;';title.innerHTML='<span>💼</span> Funding Wallets';dialog.append(title);
  const note=document.createElement('p');note.style.cssText='font-size:12px;color:#94a3b8;line-height:1.45;margin:0 0 16px;';note.textContent='Trades and withdrawals use the selected wallet. Display conversion does not transfer funds between these accounts.';dialog.append(note);
  const btnContainer=document.createElement('div');btnContainer.style.cssText='display:flex;flex-direction:column;gap:8px;margin-bottom:16px;';dialog.append(btnContainer);
  const close=document.createElement('button');close.style.cssText='width:100%;padding:10px 16px;background:#1e293b;color:#94a3b8;border:1px solid #334155;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;';close.textContent='Cancel';close.onclick=()=>dialog.close();dialog.append(close);dialog.onclose=()=>dialog.remove();document.body.append(dialog);dialog.showModal();
  
  const targetCurrencies = Array.from(new Set(['USD', 'USDT', 'INR', ...extraCurrencies.map(c => (c || '').toUpperCase().trim())])).filter(Boolean);
  for(const currency of targetCurrencies) {
    try {const balance=await client.getBalance(currency);if(!dialog.isConnected)return;
      if(balance.currency!==currency || balance.account_mode==='DEMO')continue;
      const sym = getCurrencySymbol(currency);
      const button=document.createElement('button');button.style.cssText='padding:12px 14px;background:#141c2a;border:1px solid #233044;border-radius:10px;color:#ffffff;font-size:13px;font-weight:700;display:flex;justify-content:space-between;align-items:center;cursor:pointer;transition:all 0.15s;';
      button.innerHTML=`<span>${currency} Wallet</span><strong style="color:#10b981;">${sym}${Number(balance.available_balance || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>`;
      button.onmouseover=()=>{button.style.borderColor='#38bdf8';button.style.background='#1b2638';};
      button.onmouseout=()=>{button.style.borderColor='#233044';button.style.background='#141c2a';};
      button.onclick=()=>{
        try {
          if (typeof (client as any).setPreferredCurrency === 'function') {
            (client as any).setPreferredCurrency(currency).catch(() => null);
          }
        } catch {}
        selected(currency);
        dialog.close();
      };btnContainer.append(button);
    }catch{/* Disabled or unavailable wallets are not selectable. */}
  }
}

const requests = new WeakMap<HTMLElement, string>();

export async function refreshDisplayEstimate(client: Client, host: HTMLElement, base: string, rawAmount: string | number) {
  const cleanAmountStr = String(rawAmount || '0').replace(/[^0-9.]/g, '');
  const numericVal = parseFloat(cleanAmountStr);
  const amount = isNaN(numericVal) ? '0.00' : numericVal.toFixed(2);
  const target = localStorage.getItem('starx_display_currency') || base;
  const key = [base, amount, target].join(':');
  if (requests.get(host) === key) return;
  requests.set(host, key);

  let label = host.parentElement?.querySelector<HTMLElement>('[data-display-estimate]');
  if (!label) {
    label = document.createElement('small');
    label.dataset.displayEstimate = 'true';
    label.style.cssText = 'font-size:11px;color:#94a3b8;margin-left:6px;font-weight:600;display:none;';
    host.after(label);
  }

  if (target === base) {
    const isIndia = localStorage.getItem('starx_detected_country') === 'IN';
    if (isIndia && base === 'USD') {
      try {
        const res = await client.getDisplayCurrency(base, amount, 'INR');
        if (requests.get(host) !== key || !host.isConnected) return;
        if (res?.amount && !isNaN(Number(res.amount))) {
          localStorage.setItem('starx_fx_USD_INR', res.rate || '87.50');
          const num = Number(res.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          label.textContent = ` (≈ ₹${num})`;
          label.title = `Estimated local currency value at 1 USD ≈ ₹${Number(res.rate).toFixed(2)} (${res.source})`;
          label.style.display = 'inline';
          return;
        }
      } catch {}
    }
    label.textContent = '';
    label.style.display = 'none';
    return;
  }

  try {
    const result = await client.getDisplayCurrency(base, amount, target);
    if (requests.get(host) !== key || !host.isConnected) return;
    if (result?.rate && base === 'USD' && target === 'INR') {
      localStorage.setItem('starx_fx_USD_INR', result.rate);
    }
    const val = result?.amount && !isNaN(Number(result.amount)) ? Number(result.amount) : Number(amount);
    const num = val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    label.textContent = ` (≈ ${CURRENCY_META[target]?.symbol || ''}${num} ${target})`;
    label.title = `Wallet and trades remain in ${base}. Conversion rate: 1 ${base} = ${Number(result?.rate || 1).toFixed(2)} ${result?.currency || target} (${result?.source || 'FX'})`;
    label.style.display = 'inline';
  } catch {
    if (requests.get(host) === key) {
      label.textContent = '';
      label.style.display = 'none';
      requests.delete(host);
    }
  }
}

export interface CurrencyInfo {
  code: string;
  name: string;
  symbol: string;
  flag: string;
  country: string;
  region: string;
  popular?: boolean;
}

export const WORLD_CURRENCY_DATA: Record<string, CurrencyInfo> = {
  USD: { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸', country: 'United States, USA, America, New York', region: 'Americas', popular: true },
  INR: { code: 'INR', name: 'Indian Rupee', symbol: '₹', flag: '🇮🇳', country: 'India, Bharat, Mumbai, Delhi, Bangalore', region: 'Asia', popular: true },
  EUR: { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺', country: 'European Union, Germany, France, Italy, Spain, Netherlands', region: 'Europe', popular: true },
  GBP: { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧', country: 'United Kingdom, Great Britain, UK, England, London', region: 'Europe', popular: true },
  USDT: { code: 'USDT', name: 'Tether USD', symbol: '₮', flag: '💎', country: 'Tether, Crypto, Blockchain, Stablecoin', region: 'Crypto', popular: true },
  USDC: { code: 'USDC', name: 'USD Coin', symbol: 'USDC', flag: '🔷', country: 'Circle, Crypto, Stablecoin', region: 'Crypto', popular: true },
  UZS: { code: 'UZS', name: 'Uzbekistani Som', symbol: "so'm", flag: '🇺🇿', country: 'Uzbekistan, Tashkent, Samarkand', region: 'Asia', popular: true },
  JPY: { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵', country: 'Japan, Tokyo, Osaka', region: 'Asia', popular: true },
  BRL: { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', flag: '🇧🇷', country: 'Brazil, Brasil, Sao Paulo, Rio', region: 'Americas', popular: true },
  AED: { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪', country: 'United Arab Emirates, Dubai, Abu Dhabi, UAE', region: 'Middle East', popular: true },
  VND: { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', flag: '🇻🇳', country: 'Vietnam, Hanoi, Ho Chi Minh', region: 'Asia', popular: true },
  IDR: { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', flag: '🇮🇩', country: 'Indonesia, Jakarta, Bali', region: 'Asia', popular: true },
  TRY: { code: 'TRY', name: 'Turkish Lira', symbol: '₺', flag: '🇹🇷', country: 'Turkey, Turkiye, Istanbul, Ankara', region: 'Europe', popular: true },
  SAR: { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', flag: '🇸🇦', country: 'Saudi Arabia, Riyadh, Jeddah', region: 'Middle East', popular: true },
  NGN: { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', flag: '🇳🇬', country: 'Nigeria, Lagos, Abuja', region: 'Africa', popular: true },
  ZAR: { code: 'ZAR', name: 'South African Rand', symbol: 'R', flag: '🇿🇦', country: 'South Africa, Johannesburg, Cape Town', region: 'Africa', popular: true },
  CAD: { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', flag: '🇨🇦', country: 'Canada, Toronto, Vancouver, Montreal', region: 'Americas', popular: true },
  AUD: { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺', country: 'Australia, Sydney, Melbourne', region: 'Oceania', popular: true },
  CHF: { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', flag: '🇨🇭', country: 'Switzerland, Zurich, Geneva', region: 'Europe', popular: true },
  SGD: { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬', country: 'Singapore', region: 'Asia', popular: true },
  MYR: { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', flag: '🇲🇾', country: 'Malaysia, Kuala Lumpur', region: 'Asia', popular: true },
  THB: { code: 'THB', name: 'Thai Baht', symbol: '฿', flag: '🇹🇭', country: 'Thailand, Bangkok, Phuket', region: 'Asia', popular: true },
  PHP: { code: 'PHP', name: 'Philippine Peso', symbol: '₱', flag: '🇵🇭', country: 'Philippines, Manila, Cebu', region: 'Asia', popular: true },
  KRW: { code: 'KRW', name: 'South Korean Won', symbol: '₩', flag: '🇰🇷', country: 'South Korea, Seoul', region: 'Asia', popular: true },
  KZT: { code: 'KZT', name: 'Kazakhstani Tenge', symbol: '₸', flag: '🇰🇿', country: 'Kazakhstan, Almaty, Astana', region: 'Asia', popular: true },
  EGP: { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', flag: '🇪🇬', country: 'Egypt, Cairo, Alexandria', region: 'Africa', popular: true },
  PKR: { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨', flag: '🇵🇰', country: 'Pakistan, Karachi, Lahore, Islamabad', region: 'Asia', popular: true },
  BDT: { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', flag: '🇧🇩', country: 'Bangladesh, Dhaka', region: 'Asia', popular: true },
  MXN: { code: 'MXN', name: 'Mexican Peso', symbol: 'Mex$', flag: '🇲🇽', country: 'Mexico, Mexico City', region: 'Americas', popular: true },
  COP: { code: 'COP', name: 'Colombian Peso', symbol: 'COL$', flag: '🇨🇴', country: 'Colombia, Bogota, Medellin', region: 'Americas', popular: true },
  CLP: { code: 'CLP', name: 'Chilean Peso', symbol: 'CLP$', flag: '🇨🇱', country: 'Chile, Santiago', region: 'Americas', popular: false },
  QAR: { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق', flag: '🇶🇦', country: 'Qatar, Doha', region: 'Middle East', popular: true },
  KWD: { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', flag: '🇰🇼', country: 'Kuwait, Kuwait City', region: 'Middle East', popular: true },
  BHD: { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب', flag: '🇧🇭', country: 'Bahrain, Manama', region: 'Middle East', popular: false },
  OMR: { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع.', flag: '🇴🇲', country: 'Oman, Muscat', region: 'Middle East', popular: false },
  JOD: { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا', flag: '🇯🇴', country: 'Jordan, Amman', region: 'Middle East', popular: false },
  ILS: { code: 'ILS', name: 'Israeli New Shekel', symbol: '₪', flag: '🇮🇱', country: 'Israel, Tel Aviv, Jerusalem', region: 'Middle East', popular: false },
  NZD: { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', flag: '🇳🇿', country: 'New Zealand, Auckland, Wellington', region: 'Oceania', popular: true },
  SEK: { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', flag: '🇸🇪', country: 'Sweden, Stockholm', region: 'Europe', popular: false },
  NOK: { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', flag: '🇳🇴', country: 'Norway, Oslo', region: 'Europe', popular: false },
  DKK: { code: 'DKK', name: 'Danish Krone', symbol: 'kr', flag: '🇩🇰', country: 'Denmark, Copenhagen', region: 'Europe', popular: false },
  PLN: { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', flag: '🇵🇱', country: 'Poland, Warsaw, Krakow', region: 'Europe', popular: true },
  CZK: { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', flag: '🇨🇿', country: 'Czech Republic, Czechia, Prague', region: 'Europe', popular: false },
  HUF: { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', flag: '🇭🇺', country: 'Hungary, Budapest', region: 'Europe', popular: false },
  RON: { code: 'RON', name: 'Romanian Leu', symbol: 'lei', flag: '🇷🇴', country: 'Romania, Bucharest', region: 'Europe', popular: false },
  BGN: { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв', flag: '🇧🇬', country: 'Bulgaria, Sofia', region: 'Europe', popular: false },
  HRK: { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn', flag: '🇭🇷', country: 'Croatia, Zagreb', region: 'Europe', popular: false },
  RSD: { code: 'RSD', name: 'Serbian Dinar', symbol: 'дин.', flag: '🇷🇸', country: 'Serbia, Belgrade', region: 'Europe', popular: false },
  UAH: { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', flag: '🇺🇦', country: 'Ukraine, Kyiv', region: 'Europe', popular: false },
  GEL: { code: 'GEL', name: 'Georgian Lari', symbol: '₾', flag: '🇬🇪', country: 'Georgia, Tbilisi', region: 'Asia', popular: false },
  AZN: { code: 'AZN', name: 'Azerbaijani Manat', symbol: '₼', flag: '🇦🇿', country: 'Azerbaijan, Baku', region: 'Asia', popular: false },
  AMD: { code: 'AMD', name: 'Armenian Dram', symbol: '֏', flag: '🇦🇲', country: 'Armenia, Yerevan', region: 'Asia', popular: false },
  BYN: { code: 'BYN', name: 'Belarusian Ruble', symbol: 'Br', flag: '🇧🇾', country: 'Belarus, Minsk', region: 'Europe', popular: false },
  RUB: { code: 'RUB', name: 'Russian Ruble', symbol: '₽', flag: '🇷🇺', country: 'Russia, Moscow, Saint Petersburg', region: 'Europe', popular: false },
  CNY: { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳', country: 'China, Beijing, Shanghai', region: 'Asia', popular: true },
  CNH: { code: 'CNH', name: 'Chinese Yuan Offshore', symbol: '¥', flag: '🇨🇳', country: 'China Offshore, Hong Kong', region: 'Asia', popular: false },
  HKD: { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', flag: '🇭🇰', country: 'Hong Kong', region: 'Asia', popular: true },
  TWD: { code: 'TWD', name: 'New Taiwan Dollar', symbol: 'NT$', flag: '🇹🇼', country: 'Taiwan, Taipei', region: 'Asia', popular: false },
  LKR: { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs', flag: '🇱🇰', country: 'Sri Lanka, Colombo', region: 'Asia', popular: false },
  NPR: { code: 'NPR', name: 'Nepalese Rupee', symbol: 'रू', flag: '🇳🇵', country: 'Nepal, Kathmandu', region: 'Asia', popular: false },
  MMK: { code: 'MMK', name: 'Myanmar Kyat', symbol: 'K', flag: '🇲🇲', country: 'Myanmar, Burma, Yangon', region: 'Asia', popular: false },
  KHR: { code: 'KHR', name: 'Cambodian Riel', symbol: '៛', flag: '🇰🇭', country: 'Cambodia, Phnom Penh', region: 'Asia', popular: false },
  LAK: { code: 'LAK', name: 'Lao Kip', symbol: '₭', flag: '🇱🇦', country: 'Laos, Vientiane', region: 'Asia', popular: false },
  MNT: { code: 'MNT', name: 'Mongolian Tugrik', symbol: '₮', flag: '🇲🇳', country: 'Mongolia, Ulaanbaatar', region: 'Asia', popular: false },
  BND: { code: 'BND', name: 'Brunei Dollar', symbol: 'B$', flag: '🇧🇳', country: 'Brunei, Bandar Seri Begawan', region: 'Asia', popular: false },
  MOP: { code: 'MOP', name: 'Macanese Pataca', symbol: 'MOP$', flag: '🇲🇴', country: 'Macau', region: 'Asia', popular: false },
  KGS: { code: 'KGS', name: 'Kyrgyzstani Som', symbol: 'с', flag: '🇰🇬', country: 'Kyrgyzstan, Bishkek', region: 'Asia', popular: false },
  TJS: { code: 'TJS', name: 'Tajikistani Somoni', symbol: 'ЅМ', flag: '🇹🇯', country: 'Tajikistan, Dushanbe', region: 'Asia', popular: false },
  TMT: { code: 'TMT', name: 'Turkmenistani Manat', symbol: 'T', flag: '🇹🇲', country: 'Turkmenistan, Ashgabat', region: 'Asia', popular: false },
  AFN: { code: 'AFN', name: 'Afghan Afghani', symbol: '؋', flag: '🇦🇫', country: 'Afghanistan, Kabul', region: 'Asia', popular: false },
  IQD: { code: 'IQD', name: 'Iraqi Dinar', symbol: 'ع.د', flag: '🇮🇶', country: 'Iraq, Baghdad', region: 'Middle East', popular: false },
  IRR: { code: 'IRR', name: 'Iranian Rial', symbol: '﷼', flag: '🇮🇷', country: 'Iran, Tehran', region: 'Middle East', popular: false },
  LBP: { code: 'LBP', name: 'Lebanese Pound', symbol: 'ل.ل', flag: '🇱🇧', country: 'Lebanon, Beirut', region: 'Middle East', popular: false },
  SYP: { code: 'SYP', name: 'Syrian Pound', symbol: '£S', flag: '🇸🇾', country: 'Syria, Damascus', region: 'Middle East', popular: false },
  YER: { code: 'YER', name: 'Yemeni Rial', symbol: '﷼', flag: '🇾🇪', country: 'Yemen, Sanaa', region: 'Middle East', popular: false },
  KES: { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', flag: '🇰🇪', country: 'Kenya, Nairobi', region: 'Africa', popular: true },
  GHS: { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵', flag: '🇬🇭', country: 'Ghana, Accra', region: 'Africa', popular: true },
  UGX: { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh', flag: '🇺🇬', country: 'Uganda, Kampala', region: 'Africa', popular: false },
  TZS: { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', flag: '🇹🇿', country: 'Tanzania, Dar es Salaam, Dodoma', region: 'Africa', popular: false },
  RWF: { code: 'RWF', name: 'Rwandan Franc', symbol: 'FRw', flag: '🇷🇼', country: 'Rwanda, Kigali', region: 'Africa', popular: false },
  ETB: { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br', flag: '🇪🇹', country: 'Ethiopia, Addis Ababa', region: 'Africa', popular: false },
  MAD: { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.', flag: '🇲🇦', country: 'Morocco, Casablanca, Rabat', region: 'Africa', popular: true },
  DZD: { code: 'DZD', name: 'Algerian Dinar', symbol: 'د.ج', flag: '🇩🇿', country: 'Algeria, Algiers', region: 'Africa', popular: false },
  TND: { code: 'TND', name: 'Tunisian Dinar', symbol: 'د.ت', flag: '🇹🇳', country: 'Tunisia, Tunis', region: 'Africa', popular: false },
  LYD: { code: 'LYD', name: 'Libyan Dinar', symbol: 'ل.د', flag: '🇱🇾', country: 'Libya, Tripoli', region: 'Africa', popular: false },
  SDG: { code: 'SDG', name: 'Sudanese Pound', symbol: 'ج.س.', flag: '🇸🇩', country: 'Sudan, Khartoum', region: 'Africa', popular: false },
  SSP: { code: 'SSP', name: 'South Sudanese Pound', symbol: '£', flag: '🇸🇸', country: 'South Sudan, Juba', region: 'Africa', popular: false },
  MZN: { code: 'MZN', name: 'Mozambican Metical', symbol: 'MT', flag: '🇲🇿', country: 'Mozambique, Maputo', region: 'Africa', popular: false },
  AOA: { code: 'AOA', name: 'Angolan Kwanza', symbol: 'Kz', flag: '🇦🇴', country: 'Angola, Luanda', region: 'Africa', popular: false },
  ZMW: { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZK', flag: '🇿🇲', country: 'Zambia, Lusaka', region: 'Africa', popular: false },
  BWP: { code: 'BWP', name: 'Botswana Pula', symbol: 'P', flag: '🇧🇼', country: 'Botswana, Gaborone', region: 'Africa', popular: false },
  NAD: { code: 'NAD', name: 'Namibian Dollar', symbol: 'N$', flag: '🇳🇦', country: 'Namibia, Windhoek', region: 'Africa', popular: false },
  LSL: { code: 'LSL', name: 'Lesotho Loti', symbol: 'L', flag: '🇱🇸', country: 'Lesotho, Maseru', region: 'Africa', popular: false },
  SZL: { code: 'SZL', name: 'Swazi Lilangeni', symbol: 'L', flag: '🇸🇿', country: 'Eswatini, Swaziland, Mbabane', region: 'Africa', popular: false },
  MWK: { code: 'MWK', name: 'Malawian Kwacha', symbol: 'MK', flag: '🇲🇼', country: 'Malawi, Lilongwe', region: 'Africa', popular: false },
  MUR: { code: 'MUR', name: 'Mauritian Rupee', symbol: '₨', flag: '🇲🇺', country: 'Mauritius, Port Louis', region: 'Africa', popular: false },
  SCR: { code: 'SCR', name: 'Seychellois Rupee', symbol: 'SR', flag: '🇸🇨', country: 'Seychelles, Victoria', region: 'Africa', popular: false },
  MGA: { code: 'MGA', name: 'Malagasy Ariary', symbol: 'Ar', flag: '🇲🇬', country: 'Madagascar, Antananarivo', region: 'Africa', popular: false },
  KMF: { code: 'KMF', name: 'Comorian Franc', symbol: 'CF', flag: '🇰🇲', country: 'Comoros, Moroni', region: 'Africa', popular: false },
  SOS: { code: 'SOS', name: 'Somali Shilling', symbol: 'Sh.So.', flag: '🇸🇴', country: 'Somalia, Mogadishu', region: 'Africa', popular: false },
  DJF: { code: 'DJF', name: 'Djiboutian Franc', symbol: 'Fdj', flag: '🇩🇯', country: 'Djibouti', region: 'Africa', popular: false },
  ERN: { code: 'ERN', name: 'Eritrean Nakfa', symbol: 'Nfk', flag: '🇪🇷', country: 'Eritrea, Asmara', region: 'Africa', popular: false },
  MRU: { code: 'MRU', name: 'Mauritanian Ouguiya', symbol: 'UM', flag: '🇲🇷', country: 'Mauritania, Nouakchott', region: 'Africa', popular: false },
  GMD: { code: 'GMD', name: 'Gambian Dalasi', symbol: 'D', flag: '🇬🇲', country: 'Gambia, Banjul', region: 'Africa', popular: false },
  GNF: { code: 'GNF', name: 'Guinean Franc', symbol: 'FG', flag: '🇬🇳', country: 'Guinea, Conakry', region: 'Africa', popular: false },
  SLE: { code: 'SLE', name: 'Sierra Leonean Leone', symbol: 'Le', flag: '🇸🇱', country: 'Sierra Leone, Freetown', region: 'Africa', popular: false },
  SLL: { code: 'SLL', name: 'Sierra Leone Old Leone', symbol: 'Le', flag: '🇸🇱', country: 'Sierra Leone', region: 'Africa', popular: false },
  LRD: { code: 'LRD', name: 'Liberian Dollar', symbol: 'L$', flag: '🇱🇷', country: 'Liberia, Monrovia', region: 'Africa', popular: false },
  CDF: { code: 'CDF', name: 'Congolese Franc', symbol: 'FC', flag: '🇨🇩', country: 'Democratic Republic of Congo, Kinshasa', region: 'Africa', popular: false },
  XAF: { code: 'XAF', name: 'Central African CFA Franc', symbol: 'FCFA', flag: '🇨🇲', country: 'Cameroon, Central African Republic, Chad, Congo, Gabon', region: 'Africa', popular: false },
  XOF: { code: 'XOF', name: 'West African CFA Franc', symbol: 'CFA', flag: '🇸🇳', country: 'Senegal, Ivory Coast, Benin, Burkina Faso, Mali, Niger, Togo', region: 'Africa', popular: false },
  CVE: { code: 'CVE', name: 'Cape Verdean Escudo', symbol: '$', flag: '🇨🇻', country: 'Cape Verde, Praia', region: 'Africa', popular: false },
  STN: { code: 'STN', name: 'Sao Tome and Principe Dobra', symbol: 'Db', flag: '🇸🇹', country: 'Sao Tome and Principe', region: 'Africa', popular: false },
  BIF: { code: 'BIF', name: 'Burundian Franc', symbol: 'FBu', flag: '🇧🇮', country: 'Burundi, Gitega', region: 'Africa', popular: false },
  ZWG: { code: 'ZWG', name: 'Zimbabwean Gold', symbol: 'ZiG', flag: '🇿🇼', country: 'Zimbabwe, Harare', region: 'Africa', popular: false },
  ZWL: { code: 'ZWL', name: 'Zimbabwean Dollar', symbol: 'Z$', flag: '🇿🇼', country: 'Zimbabwe', region: 'Africa', popular: false },
  ARS: { code: 'ARS', name: 'Argentine Peso', symbol: '$', flag: '🇦🇷', country: 'Argentina, Buenos Aires', region: 'Americas', popular: true },
  PEN: { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/', flag: '🇵🇪', country: 'Peru, Lima', region: 'Americas', popular: false },
  UYU: { code: 'UYU', name: 'Uruguayan Peso', symbol: '$U', flag: '🇺🇾', country: 'Uruguay, Montevideo', region: 'Americas', popular: false },
  PYG: { code: 'PYG', name: 'Paraguayan Guarani', symbol: '₲', flag: '🇵🇾', country: 'Paraguay, Asuncion', region: 'Americas', popular: false },
  BOB: { code: 'BOB', name: 'Bolivian Boliviano', symbol: 'Bs.', flag: '🇧🇴', country: 'Bolivia, La Paz, Sucre', region: 'Americas', popular: false },
  VES: { code: 'VES', name: 'Venezuelan Bolivar', symbol: 'Bs.', flag: '🇻🇪', country: 'Venezuela, Caracas', region: 'Americas', popular: false },
  CRC: { code: 'CRC', name: 'Costa Rican Colon', symbol: '₡', flag: '🇨🇷', country: 'Costa Rica, San Jose', region: 'Americas', popular: false },
  DOP: { code: 'DOP', name: 'Dominican Peso', symbol: 'RD$', flag: '🇩🇴', country: 'Dominican Republic, Santo Domingo', region: 'Americas', popular: false },
  GTQ: { code: 'GTQ', name: 'Guatemalan Quetzal', symbol: 'Q', flag: '🇬🇹', country: 'Guatemala, Guatemala City', region: 'Americas', popular: false },
  HNL: { code: 'HNL', name: 'Honduran Lempira', symbol: 'L', flag: '🇭🇳', country: 'Honduras, Tegucigalpa', region: 'Americas', popular: false },
  NIO: { code: 'NIO', name: 'Nicaraguan Cordoba', symbol: 'C$', flag: '🇳🇮', country: 'Nicaragua, Managua', region: 'Americas', popular: false },
  PAB: { code: 'PAB', name: 'Panamanian Balboa', symbol: 'B/.', flag: '🇵🇦', country: 'Panama, Panama City', region: 'Americas', popular: false },
  JMD: { code: 'JMD', name: 'Jamaican Dollar', symbol: 'J$', flag: '🇯🇲', country: 'Jamaica, Kingston', region: 'Americas', popular: false },
  TTD: { code: 'TTD', name: 'Trinidad and Tobago Dollar', symbol: 'TT$', flag: '🇹🇹', country: 'Trinidad and Tobago, Port of Spain', region: 'Americas', popular: false },
  BSD: { code: 'BSD', name: 'Bahamian Dollar', symbol: 'B$', flag: '🇧🇸', country: 'Bahamas, Nassau', region: 'Americas', popular: false },
  BBD: { code: 'BBD', name: 'Barbadian Dollar', symbol: 'Bds$', flag: '🇧🇧', country: 'Barbados, Bridgetown', region: 'Americas', popular: false },
  BZD: { code: 'BZD', name: 'Belize Dollar', symbol: 'BZ$', flag: '🇧🇿', country: 'Belize, Belmopan', region: 'Americas', popular: false },
  BMD: { code: 'BMD', name: 'Bermudian Dollar', symbol: '$', flag: '🇧🇲', country: 'Bermuda, Hamilton', region: 'Americas', popular: false },
  KYD: { code: 'KYD', name: 'Cayman Islands Dollar', symbol: 'CI$', flag: '🇰🇾', country: 'Cayman Islands, George Town', region: 'Americas', popular: false },
  GYD: { code: 'GYD', name: 'Guyanese Dollar', symbol: 'G$', flag: '🇬🇾', country: 'Guyana, Georgetown', region: 'Americas', popular: false },
  SRD: { code: 'SRD', name: 'Surinamese Dollar', symbol: '$', flag: '🇸🇷', country: 'Suriname, Paramaribo', region: 'Americas', popular: false },
  HTG: { code: 'HTG', name: 'Haitian Gourde', symbol: 'G', flag: '🇭🇹', country: 'Haiti, Port-au-Prince', region: 'Americas', popular: false },
  CUP: { code: 'CUP', name: 'Cuban Peso', symbol: '₱', flag: '🇨🇺', country: 'Cuba, Havana', region: 'Americas', popular: false },
  AWG: { code: 'AWG', name: 'Aruban Florin', symbol: 'ƒ', flag: '🇦🇼', country: 'Aruba, Oranjestad', region: 'Americas', popular: false },
  ANG: { code: 'ANG', name: 'Netherlands Antillean Guilder', symbol: 'ƒ', flag: '🇨🇼', country: 'Curacao, Sint Maarten', region: 'Americas', popular: false },
  XCD: { code: 'XCD', name: 'East Caribbean Dollar', symbol: 'EC$', flag: '🇦🇬', country: 'Antigua, Dominica, Grenada, Saint Lucia, Saint Kitts', region: 'Americas', popular: false },
  XCG: { code: 'XCG', name: 'Caribbean Guilder', symbol: 'Cg', flag: '🇨🇼', country: 'Curacao, Sint Maarten', region: 'Americas', popular: false },
  CLF: { code: 'CLF', name: 'Chilean Unidad de Fomento', symbol: 'UF', flag: '🇨🇱', country: 'Chile', region: 'Americas', popular: false },
  ALL: { code: 'ALL', name: 'Albanian Lek', symbol: 'L', flag: '🇦🇱', country: 'Albania, Tirana', region: 'Europe', popular: false },
  BAM: { code: 'BAM', name: 'Bosnia Convertible Mark', symbol: 'KM', flag: '🇧🇦', country: 'Bosnia and Herzegovina, Sarajevo', region: 'Europe', popular: false },
  MKD: { code: 'MKD', name: 'Macedonian Denar', symbol: 'ден', flag: '🇲🇰', country: 'North Macedonia, Skopje', region: 'Europe', popular: false },
  MDL: { code: 'MDL', name: 'Moldovan Leu', symbol: 'L', flag: '🇲🇩', country: 'Moldova, Chisinau', region: 'Europe', popular: false },
  ISK: { code: 'ISK', name: 'Icelandic Krona', symbol: 'kr', flag: '🇮🇸', country: 'Iceland, Reykjavik', region: 'Europe', popular: false },
  GIP: { code: 'GIP', name: 'Gibraltar Pound', symbol: '£', flag: '🇬🇮', country: 'Gibraltar', region: 'Europe', popular: false },
  FOK: { code: 'FOK', name: 'Faroese Krona', symbol: 'kr', flag: '🇫🇴', country: 'Faroe Islands, Torshavn', region: 'Europe', popular: false },
  GGP: { code: 'GGP', name: 'Guernsey Pound', symbol: '£', flag: '🇬🇬', country: 'Guernsey, Saint Peter Port', region: 'Europe', popular: false },
  IMP: { code: 'IMP', name: 'Isle of Man Pound', symbol: '£', flag: '🇮🇲', country: 'Isle of Man, Douglas', region: 'Europe', popular: false },
  JEP: { code: 'JEP', name: 'Jersey Pound', symbol: '£', flag: '🇯🇪', country: 'Jersey, Saint Helier', region: 'Europe', popular: false },
  PGK: { code: 'PGK', name: 'Papua New Guinean Kina', symbol: 'K', flag: '🇵🇬', country: 'Papua New Guinea, Port Moresby', region: 'Oceania', popular: false },
  FJD: { code: 'FJD', name: 'Fijian Dollar', symbol: 'FJ$', flag: '🇫🇯', country: 'Fiji, Suva', region: 'Oceania', popular: false },
  SBD: { code: 'SBD', name: 'Solomon Islands Dollar', symbol: 'SI$', flag: '🇸🇧', country: 'Solomon Islands, Honiara', region: 'Oceania', popular: false },
  VUV: { code: 'VUV', name: 'Vanuatu Vatu', symbol: 'VT', flag: '🇻🇺', country: 'Vanuatu, Port Vila', region: 'Oceania', popular: false },
  WST: { code: 'WST', name: 'Samoan Tala', symbol: 'WS$', flag: '🇼🇸', country: 'Samoa, Apia', region: 'Oceania', popular: false },
  TOP: { code: 'TOP', name: 'Tongan Paanga', symbol: 'T$', flag: '🇹🇴', country: 'Tonga, Nukualofa', region: 'Oceania', popular: false },
  KID: { code: 'KID', name: 'Kiribati Dollar', symbol: '$', flag: '🇰🇮', country: 'Kiribati, Tarawa', region: 'Oceania', popular: false },
  TVD: { code: 'TVD', name: 'Tuvaluan Dollar', symbol: '$', flag: '🇹🇻', country: 'Tuvalu, Funafuti', region: 'Oceania', popular: false },
  XPF: { code: 'XPF', name: 'CFP Franc', symbol: '₣', flag: '🇵🇫', country: 'French Polynesia, New Caledonia, Wallis and Futuna', region: 'Oceania', popular: false },
  SHP: { code: 'SHP', name: 'Saint Helena Pound', symbol: '£', flag: '🇸🇭', country: 'Saint Helena, Jamestown', region: 'Africa', popular: false },
  FKP: { code: 'FKP', name: 'Falkland Islands Pound', symbol: '£', flag: '🇫🇰', country: 'Falkland Islands, Stanley', region: 'Americas', popular: false },
  BTN: { code: 'BTN', name: 'Bhutanese Ngultrum', symbol: 'Nu.', flag: '🇧🇹', country: 'Bhutan, Thimphu', region: 'Asia', popular: false },
  MVR: { code: 'MVR', name: 'Maldivian Rufiyaa', symbol: 'Rf', flag: '🇲🇻', country: 'Maldives, Male', region: 'Asia', popular: false },
  XDR: { code: 'XDR', name: 'Special Drawing Rights', symbol: 'SDR', flag: '🌐', country: 'International Monetary Fund, IMF', region: 'Global', popular: false },
};

// Backward-compatible alias
export const CURRENCY_META = WORLD_CURRENCY_DATA;

export async function chooseDisplayCurrency(
  client: Client,
  base: string,
  rawAmount: string | number,
  updated: (chosenCurrency?: string) => void
) {
  const cleanAmountStr = String(rawAmount || '0').replace(/[^0-9.]/g, '');
  const numericVal = parseFloat(cleanAmountStr);
  const amount = isNaN(numericVal) ? '0.00' : numericVal.toFixed(2);
  const baseCurr = (base || 'USD').toUpperCase().trim();

  // Create Modal Shell
  const dialog = document.createElement('dialog');
  dialog.className = 'starx-currency-selector-dialog';
  dialog.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);margin:0;background:#090e18;color:#f8fafc;padding:0;border:1px solid #1e293b;border-radius:20px;box-shadow:0 25px 70px rgba(0,0,0,0.92),0 0 35px rgba(56,189,248,0.18);max-width:500px;width:92vw;max-height:86vh;display:flex;flex-direction:column;overflow:hidden;z-index:99999;font-family:inherit;';

  // Backdrop click to dismiss
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });
  dialog.onclose = () => dialog.remove();

  // 1. Header (Sticky)
  const header = document.createElement('div');
  header.style.cssText = 'padding:18px 20px 12px;border-bottom:1px solid #162032;flex-shrink:0;background:#0c1322;';

  const titleRow = document.createElement('div');
  titleRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;';
  titleRow.innerHTML = `
    <h3 style="margin:0;font-size:16px;font-weight:700;color:#ffffff;display:flex;align-items:center;gap:8px;">
      <span>🌐</span> Balance Display Currency
    </h3>
    <button id="curr-modal-close-x" style="background:none;border:none;color:#64748b;font-size:18px;cursor:pointer;padding:4px 8px;border-radius:6px;line-height:1;transition:color 0.15s;" title="Close">✕</button>
  `;
  header.append(titleRow);

  const desc = document.createElement('p');
  desc.style.cssText = 'font-size:12px;color:#94a3b8;line-height:1.45;margin:0 0 12px;';
  desc.textContent = `Wallet balance and trading contracts remain securely in ${baseCurr}. Display currency converts figures for visual convenience without transferring funds.`;
  header.append(desc);

  // Search Bar Container
  const searchWrap = document.createElement('div');
  searchWrap.style.cssText = 'position:relative;display:flex;align-items:center;margin-bottom:10px;';
  searchWrap.innerHTML = `
    <span style="position:absolute;left:12px;color:#64748b;font-size:14px;pointer-events:none;">🔍</span>
    <input id="curr-search-input" type="text" placeholder="Search currency, code or country (e.g. UZS, Yen, Euro, Uzbekistan...)"
      style="width:100%;box-sizing:border-box;padding:9px 34px 9px 34px;background:#131c2d;border:1px solid #253347;border-radius:10px;color:#ffffff;font-size:13px;outline:none;transition:border-color 0.15s;" />
    <button id="curr-search-clear" style="display:none;position:absolute;right:10px;background:none;border:none;color:#64748b;font-size:14px;cursor:pointer;padding:4px;line-height:1;" title="Clear search">✕</button>
  `;
  header.append(searchWrap);

  // Filter Chips Row
  const chipsRow = document.createElement('div');
  chipsRow.style.cssText = 'display:flex;gap:6px;overflow-x:auto;padding-bottom:2px;-webkit-overflow-scrolling:touch;scrollbar-width:none;';
  const filterCategories = [
    { id: 'ALL', label: 'All' },
    { id: 'POPULAR', label: '⭐ Popular' },
    { id: 'Asia', label: 'Asia' },
    { id: 'Europe', label: 'Europe' },
    { id: 'Americas', label: 'Americas' },
    { id: 'Middle East', label: 'Middle East' },
    { id: 'Africa', label: 'Africa' },
  ];
  let currentFilter = 'ALL';
  filterCategories.forEach((cat) => {
    const chip = document.createElement('button');
    chip.dataset.filter = cat.id;
    chip.style.cssText = `white-space:nowrap;padding:4px 10px;border-radius:20px;font-size:11px;font-weight:600;cursor:pointer;transition:all 0.15s;border:1px solid ${cat.id === 'ALL' ? '#38bdf8' : '#223044'};background:${cat.id === 'ALL' ? '#172554' : '#101726'};color:${cat.id === 'ALL' ? '#38bdf8' : '#94a3b8'};`;
    chip.textContent = cat.label;
    chip.onclick = () => {
      currentFilter = cat.id;
      chipsRow.querySelectorAll('button').forEach((b) => {
        const isSel = (b as HTMLElement).dataset.filter === cat.id;
        (b as HTMLElement).style.borderColor = isSel ? '#38bdf8' : '#223044';
        (b as HTMLElement).style.background = isSel ? '#172554' : '#101726';
        (b as HTMLElement).style.color = isSel ? '#38bdf8' : '#94a3b8';
      });
      renderList();
    };
    chipsRow.append(chip);
  });
  header.append(chipsRow);
  dialog.append(header);

  // 2. Scrollable Currency List
  const listContainer = document.createElement('div');
  listContainer.id = 'curr-scroll-container';
  listContainer.style.cssText = 'flex:1;overflow-y:auto;padding:12px 18px;display:flex;flex-direction:column;gap:7px;max-height:54vh;overscroll-behavior:contain;';
  listContainer.innerHTML = '<div style="color:#64748b;font-size:12px;text-align:center;padding:24px;">Loading live world currency rates...</div>';
  dialog.append(listContainer);

  // 3. Footer (Sticky)
  const footer = document.createElement('div');
  footer.style.cssText = 'padding:12px 20px;border-top:1px solid #162032;display:flex;justify-content:space-between;align-items:center;background:#0c1322;flex-shrink:0;';
  footer.innerHTML = `
    <span id="curr-count-label" style="font-size:11px;color:#64748b;font-weight:600;">168 currencies available</span>
    <button id="curr-modal-close-btn" style="padding:7px 18px;background:#1e293b;color:#e2e8f0;border:1px solid #334155;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;transition:all 0.15s;">Close</button>
  `;
  dialog.append(footer);

  document.body.append(dialog);
  dialog.showModal();

  // Wire close buttons
  dialog.querySelector('#curr-modal-close-x')?.addEventListener('click', () => dialog.close());
  dialog.querySelector('#curr-modal-close-btn')?.addEventListener('click', () => dialog.close());

  // Search input interactions
  const searchInput = dialog.querySelector<HTMLInputElement>('#curr-search-input')!;
  const clearSearchBtn = dialog.querySelector<HTMLButtonElement>('#curr-search-clear')!;

  searchInput.addEventListener('input', () => {
    clearSearchBtn.style.display = searchInput.value ? 'block' : 'none';
    renderList();
  });

  clearSearchBtn.addEventListener('click', () => {
    searchInput.value = '';
    clearSearchBtn.style.display = 'none';
    searchInput.focus();
    renderList();
  });

  let serverRates: Record<string, string> = {};
  let detectedCountry = '';

  try {
    detectedCountry = localStorage.getItem('starx_detected_country') || '';
    const res = await client.getDisplayCurrency(baseCurr, amount, '', detectedCountry);
    if (res?.rates) {
      serverRates = res.rates;
      cacheWorldFxRates(res.rates);
    }
  } catch {
    // Graceful fallback to cached / institutional rates
  }

  const activeTarget = (localStorage.getItem('starx_display_currency') || baseCurr).toUpperCase().trim();
  const allCurrenciesList = Object.values(WORLD_CURRENCY_DATA);

  // Helper function to calculate live FX conversion
  function calculateConversion(targetCurr: string): { rateNum: number; subtitle: string; formattedVal: string } {
    const meta = WORLD_CURRENCY_DATA[targetCurr] || { symbol: targetCurr, flag: '🌐', name: targetCurr, country: '' };
    if (targetCurr === baseCurr) {
      const numVal = parseFloat(amount) || 0;
      const formatted = targetCurr === 'INR'
        ? `₹ ${numVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : `${meta.symbol} ${numVal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return {
        rateNum: 1.0,
        subtitle: 'Base account currency',
        formattedVal: formatted,
      };
    }

    let rateNum = 1.0;
    const pair1 = `${baseCurr}/${targetCurr}`;
    const pair2 = `${targetCurr}/${baseCurr}`;

    if (serverRates[pair1]) {
      rateNum = parseFloat(serverRates[pair1]) || 1.0;
    } else if (serverRates[pair2]) {
      const inv = parseFloat(serverRates[pair2]);
      rateNum = inv > 0 ? 1.0 / inv : 1.0;
    } else {
      // Triangulation through USD
      const usdBase = getLiveUsdFxRate(baseCurr);
      const usdTarget = getLiveUsdFxRate(targetCurr);
      if (usdBase > 0 && usdTarget > 0) {
        rateNum = usdTarget / usdBase;
      }
    }

    const convertedAmount = (parseFloat(amount) || 0) * rateNum;
    let rateDisplay = '';
    if (rateNum >= 100) {
      rateDisplay = rateNum.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    } else if (rateNum >= 1) {
      rateDisplay = rateNum.toFixed(2);
    } else if (rateNum >= 0.001) {
      rateDisplay = rateNum.toFixed(4);
    } else {
      rateDisplay = rateNum.toFixed(6);
    }

    const countrySnippet = meta.country ? meta.country.split(',')[0].trim() : '';
    const subtitle = `${countrySnippet ? countrySnippet + ' • ' : ''}1 ${baseCurr} ≈ ${meta.symbol}${rateDisplay} (Live FX)`;

    const formattedVal = targetCurr === 'INR'
      ? `₹ ${convertedAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : `${meta.symbol} ${convertedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return { rateNum, subtitle, formattedVal };
  }

  function renderCard(item: CurrencyInfo): HTMLElement {
    const isSelected = item.code === activeTarget;
    const { subtitle, formattedVal } = calculateConversion(item.code);

    const card = document.createElement('div');
    card.className = 'currency-choice-card';
    card.dataset.currency = item.code;
    card.style.cssText = `padding:10px 14px;background:${isSelected ? '#142542' : '#111a2a'};border:1px solid ${isSelected ? '#38bdf8' : '#1e2c40'};border-radius:12px;cursor:pointer;transition:all 0.15s ease;display:flex;justify-content:space-between;align-items:center;`;

    card.onmouseover = () => {
      if (!isSelected) {
        card.style.borderColor = '#38bdf8';
        card.style.background = '#172439';
      }
    };
    card.onmouseout = () => {
      if (!isSelected) {
        card.style.borderColor = '#1e2c40';
        card.style.background = '#111a2a';
      }
    };

    card.innerHTML = `
      <div style="display:flex;align-items:center;gap:11px;min-width:0;">
        <span style="font-size:24px;line-height:1;flex-shrink:0;">${item.flag}</span>
        <div style="min-width:0;">
          <div style="font-weight:700;color:#ffffff;font-size:13px;display:flex;align-items:center;gap:6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
            ${item.name} (${item.code})
            ${isSelected ? '<span style="font-size:9px;background:#38bdf8;color:#0b111e;padding:1px 6px;border-radius:4px;font-weight:800;letter-spacing:0.3px;">ACTIVE</span>' : ''}
          </div>
          <div style="font-size:11px;color:#94a3b8;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${subtitle}</div>
        </div>
      </div>
      <div style="text-align:right;flex-shrink:0;margin-left:12px;">
        <strong style="font-size:13.5px;color:${isSelected ? '#38bdf8' : '#10b981'};font-variant-numeric:tabular-nums;">${formattedVal}</strong>
      </div>
    `;

    card.onclick = () => {
      try {
        localStorage.setItem('starx_display_currency', item.code);
        localStorage.setItem('starx_user_explicit_currency', item.code);
      } catch {}
      updated(item.code);
      dialog.close();
    };

    return card;
  }

  function renderList() {
    const q = (searchInput.value || '').trim().toLowerCase();
    listContainer.innerHTML = '';

    let filtered = allCurrenciesList;

    // Apply Region Filter if not ALL
    if (currentFilter === 'POPULAR') {
      filtered = filtered.filter((c) => c.popular || c.code === activeTarget || c.code === baseCurr);
    } else if (currentFilter !== 'ALL') {
      filtered = filtered.filter((c) => c.region === currentFilter || c.code === activeTarget);
    }

    // Apply Search Query Filter
    if (q) {
      filtered = filtered.filter((c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.country.toLowerCase().includes(q) ||
        c.symbol.toLowerCase().includes(q)
      );
    }

    const countLabel = dialog.querySelector<HTMLElement>('#curr-count-label');
    if (countLabel) {
      countLabel.textContent = `Showing ${filtered.length} of ${allCurrenciesList.length} currencies`;
    }

    if (filtered.length === 0) {
      listContainer.innerHTML = `
        <div style="text-align:center;padding:32px 16px;color:#94a3b8;">
          <div style="font-size:28px;margin-bottom:8px;">🔍</div>
          <div style="font-weight:600;font-size:13px;color:#ffffff;margin-bottom:4px;">No currencies match "${q}"</div>
          <div style="font-size:12px;">Try searching for country name, ISO code or currency name</div>
          <button id="btn-reset-search" style="margin-top:12px;padding:6px 14px;background:#1e293b;border:1px solid #334155;border-radius:8px;color:#38bdf8;font-size:12px;font-weight:700;cursor:pointer;">Clear Search</button>
        </div>
      `;
      listContainer.querySelector('#btn-reset-search')?.addEventListener('click', () => {
        searchInput.value = '';
        clearSearchBtn.style.display = 'none';
        renderList();
      });
      return;
    }

    // When no search query and 'ALL' filter, group into "Current & Popular" then "All Currencies A-Z"
    if (!q && currentFilter === 'ALL') {
      // 1. Featured Header
      const featuredHeader = document.createElement('div');
      featuredHeader.style.cssText = 'font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;margin:4px 0 2px;';
      featuredHeader.textContent = '⭐ Featured & Popular Currencies';
      listContainer.append(featuredHeader);

      // Featured items (Active currency first, then popular currencies)
      const popularCodes = ['USD', 'EUR', 'GBP', 'INR', 'JPY', 'UZS', 'BRL', 'AED', 'VND', 'IDR', 'TRY', 'SAR', 'NGN', 'ZAR', 'CAD', 'AUD', 'CHF', 'USDT'];
      const seen = new Set<string>();

      // Active target first
      const activeItem = WORLD_CURRENCY_DATA[activeTarget];
      if (activeItem) {
        listContainer.append(renderCard(activeItem));
        seen.add(activeTarget);
      }

      popularCodes.forEach((code) => {
        if (!seen.has(code) && WORLD_CURRENCY_DATA[code]) {
          seen.add(code);
          listContainer.append(renderCard(WORLD_CURRENCY_DATA[code]));
        }
      });

      // 2. All Currencies Section Header
      const allHeader = document.createElement('div');
      allHeader.style.cssText = 'font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;margin:12px 0 4px;';
      allHeader.textContent = '🌐 All World Currencies (A–Z)';
      listContainer.append(allHeader);

      // Sort rest alphabetically
      const sortedAll = [...allCurrenciesList].sort((a, b) => a.code.localeCompare(b.code));
      sortedAll.forEach((item) => {
        listContainer.append(renderCard(item));
      });
    } else {
      // Direct filtered view
      const sorted = [...filtered].sort((a, b) => {
        if (a.code === activeTarget) return -1;
        if (b.code === activeTarget) return 1;
        return a.code.localeCompare(b.code);
      });
      sorted.forEach((item) => {
        listContainer.append(renderCard(item));
      });
    }
  }

  renderList();
}
